import { getCookie } from '../../utils/helpers';
import { handle401Error, handle403Error } from '../../utils/authErrorHandler';
import request, { ResponseError } from '../../utils/request';

// Base API URL - using environment variable with fallback for local development
const API_BASE_URL =
  process.env.REACT_APP_API_V2_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2'
    : '/product-roadmap/api/v2'); // Use relative path for production

/**
 * Get authentication headers for API requests
 * Follows the same pattern as other API calls in the application
 */
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

// Interface for the moment data structure returned by the API
export interface MomentData {
  issueId: string;
  name: string;
  momentType?: string; // Add momentType at root level for Events and Key Dates
  customerAudiences?: string[];
  productGroups: string[] | string;
  categories: string[] | string;
  products?: string;
  statusUpdatedAt?: string; // Add statusUpdatedAt at root level for completion tracking
  secondaryDateStatus?: string; // Root-level secondary date status (e.g. Offer/PCO effective date), independent of showOnRoadmap
  surfaces?: string[]; // Array of surfaces (Desktop, Web, Mobile, etc.)
  genai?: boolean; // GenAI flag
  consumesCredit?: boolean; // Consumes Credit flag
  mustWin?: boolean; // Must Win flag
  parId?: string; // Pricing Operations PAR ID (Price Changes and Offers)
  generalInfo?: {
    momentType: string;
    productGroups: string;
    categories: string;
    releasePhase: string;
    reportingStatus: string;
    riskLevel?: string;
    dates: {
      targetStart: string;
      targetEnd: string;
      targetMonth?: string;
      targetQuarter?: string;
      targetEndMonth?: string;
      releaseDate?: string;
      targetDateStatus?: string; // TBD or N/A for target dates
      releaseDateStatus?: string; // TBD or N/A for release dates
      effectiveDateStatus?: string; // TBD or N/A for effective dates
      // Secondary dates for timeline display
      releaseMonth?: string;
      releaseQuarter?: string;
      effectiveDate?: string;
      effectiveMonth?: string;
      effectiveQuarter?: string;
    };
    tier: string;
    revenueImpact?: boolean; // Revenue impact flag for GA and Public Beta phases (true/false)
    event?: {
      id: string;
      name: string;
    };
    parentMoment?: string | null;
  };
  primaryContacts?: {
    productMarketingManagers: any[];
    programManagers: any[];
    productManagers: any[];
  };
  releasePhases?: Array<{
    phaseName: string;
    generalInfo: {
      momentType: string;
      riskLevel: string;
      dates: {
        targetStart?: string;
        targetEnd?: string;
        targetMonth?: string;
        targetQuarter?: string;
        targetEndMonth?: string;
        targetDateStatus?: string; // TBD or N/A for target dates
        releaseDateStatus?: string; // TBD or N/A for release dates
        effectiveDateStatus?: string; // TBD or N/A for effective dates
        // Secondary dates for timeline display
        releaseDate?: string;
        releaseMonth?: string;
        releaseQuarter?: string;
        effectiveDate?: string;
        effectiveMonth?: string;
        effectiveQuarter?: string;
      };
      tier: string;
      revenueImpact?: boolean; // Revenue impact flag for GA and Public Beta phases (true/false)
      event?: {
        id: string;
        name: string;
      };
      parentMoment?: string | null;
    };
    primaryContacts?: any;
    status: string;
    inheritMilestones?: any;
    milestoneId?: any;
    milestones?: any;
    id: string;
  }>;
  overview?: {
    // Support both old and new payload structures
    headline?: string;
    headlineUpdatedAt?: string; // Timestamp for headline updates

    // Old structure fields
    whatAreWeLaunching?: string;
    whyAreWeLaunching?: {
      selected: string[];
      content: string;
    };
    whoIsItFor?: {
      selected: string[];
      content: string;
    };
    whereWillItLaunch?: string;

    // New structure fields
    launchSummary?: string;
    goals?: {
      option: string;
      content: string;
    };
    targetAudience?: {
      options: string[];
      content: string;
    };
    launchRegion?: string;

    // Common fields
    howIsItDifferent?: string;
    strategicAlignment?: string;
    mvpStatus?: {
      selected?: string[];
      content?: string;
      option?: string;
    };

    // Legacy fields
    statusDescription?: string;
  };
  tags?: string[];
  additionalInfo?: Array<{
    id: string;
    title: string;
    url: string;
  }>;
  decisions?: Array<{
    date: string;
    id: string;
    description: string;
    link?: string;
  }>;
  daciContacts?: {
    contacts: Array<{
      id?: string;
      daciRole: string; // Driver, Approver, Contributor, Informed
      role?: string; // Product Manager, Program Manager, etc.
      name: string;
      uid?: string;
      imageUrl: string;
      _delete?: boolean;
    }>;
    roles: {
      Driver: {
        title: string;
        description: string;
        displayOrder: number;
      };
      Contributor: {
        title: string;
        description: string;
        displayOrder: number;
      };
      Approver: {
        title: string;
        description: string;
        displayOrder: number;
      };
      Informed: {
        title: string;
        description: string;
        displayOrder: number;
      };
    };
  };
  milestones?: {
    id: string;
    issueId: string;
    milestoneId: string;
    milestones: any[];
  };
  status?: string;
  inheritMilestones?: boolean;
  showOnTimeline?: boolean; // New field to control which dates to use for timeline display
  updated?: string;
  canEdit?: boolean;
  canRead?: boolean;
  roles?: string[];
  admin?: boolean;
}

// API response wrapper
export interface MomentApiResponse {
  success: boolean;
  data?: MomentData;
  error?: string;
  message?: string;
}

/**
 * Fetches moment data from the API endpoint
 * @param issueId - The unique identifier for the moment/issue
 * @returns Promise containing the moment data or error
 */
export const fetchMomentData = async (
  issueId: string
): Promise<MomentApiResponse> => {
  try {
    // Construct the API URL
    const url = `${API_BASE_URL}/moments/${encodeURIComponent(issueId)}`;

    // Get auth headers and log if token is present
    const headers = getAuthHeaders();

    // Make the API call with authentication headers
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include' // Include cookies for authentication
    });

    // Check if the response is successful
    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `❌ API Error: ${response.status} ${response.statusText}`,
        errorText
      );

      // Handle specific error codes
      switch (response.status) {
        case 404:
          return {
            success: false,
            error: 'MOMENT_NOT_FOUND',
            message: `Moment with ID '${issueId}' not found`
          };
        case 403:
          handle403Error(`Fetching moment ${issueId}`);
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'You do not have permission to access this moment'
          };
        case 401:
          handle401Error(`Fetching moment ${issueId}`);
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required to access this moment'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: 'Internal server error. Please try again later.'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: `API request failed with status ${response.status}`
          };
      }
    }

    // Parse the JSON response
    const data: MomentData = await response.json();

    // Validate that we received the expected data structure
    if (!data.issueId) {
      console.error('❌ Invalid API response: missing issueId', data);
      return {
        success: false,
        error: 'INVALID_RESPONSE',
        message: 'Invalid response format from API'
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('❌ Network error fetching moment data:', error);

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.'
      };
    }

    return {
      success: false,
      error: 'UNKNOWN_ERROR',
      message:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
};

/**
 * Updates primary contacts data via API
 * @param issueId - The moment's issue ID
 * @param primaryContactsData - The primary contacts update data
 * @returns Promise containing the update result
 */
export const updateMomentPrimaryContacts = async (
  issueId: string,
  primaryContactsData: {
    productMarketingManagers?: any[];
    programManagers?: any[];
    productManagers?: any[];
  }
): Promise<{ success: boolean; error?: string; data?: any }> => {
  try {
    const url = `${API_BASE_URL}/moments/${encodeURIComponent(issueId)}`;
    const headers = getAuthHeaders();

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        primaryContacts: primaryContactsData
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `❌ Primary contacts update failed: ${response.status}`,
        errorText
      );
      return {
        success: false,
        error: `Failed to update primary contacts: ${response.status} ${response.statusText}`
      };
    }

    const result = await response.json();

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('❌ Error updating primary contacts:', error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update primary contacts'
    };
  }
};

/**
 * Updates milestone data via API
 * @param issueId - The moment's issue ID
 * @param milestoneData - The milestone update data
 * @returns Promise containing the update result
 */
export const updateMomentMilestone = async (
  issueId: string,
  milestoneData: any
): Promise<{ success: boolean; error?: string; data?: any }> => {
  try {
    const url = `${API_BASE_URL}/moments/${encodeURIComponent(
      issueId
    )}/milestones`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(milestoneData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `❌ Milestone update failed: ${response.status}`,
        errorText
      );

      // Parse error message from API response
      let errorMessage = `Failed to update milestone: ${response.status} ${response.statusText}`;
      try {
        const errorData = JSON.parse(errorText);

        // Check for data.errors array (API validation errors format)
        if (
          errorData.data?.errors &&
          Array.isArray(errorData.data.errors) &&
          errorData.data.errors.length > 0
        ) {
          // Join all error messages from the errors array
          errorMessage = errorData.data.errors.join('; ');
        } else {
          // Fallback to message or error field
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      } catch (e) {
        // If not JSON, use the text as error message if available
        if (errorText && errorText.trim().length > 0) {
          errorMessage = errorText;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }

    const result = await response.json();

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('❌ Error updating milestone:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update milestone'
    };
  }
};

/**
 * Deletes a moment/RSD via API
 * @param issueId - The moment's issue ID to delete
 * @returns Promise containing the deletion result
 */
export const deleteMoment = async (
  issueId: string
): Promise<{
  success: boolean;
  status?: number;
  error?: string;
  message?: string;
}> => {
  try {
    const url = `${API_BASE_URL}/moments/${encodeURIComponent(issueId)}`;
    const headers = getAuthHeaders();

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });

    // 204 No Content is the success response
    if (response.status === 204) {
      // Clear cache for the deleted moment
      clearMomentCache(issueId);
      return {
        success: true,
        status: 204
      };
    }

    // Handle other status codes
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Delete failed: ${response.status}`, errorText);
      return {
        success: false,
        status: response.status,
        error: `Failed to delete RSD: ${response.status} ${response.statusText}`,
        message: errorText || 'Failed to delete RSD'
      };
    }

    return {
      success: true,
      status: response.status
    };
  } catch (error) {
    console.error('❌ Error deleting moment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete RSD',
      message: 'An unexpected error occurred while deleting the RSD'
    };
  }
};

/**
 * Deletes milestones via API
 * @param issueId - The moment's issue ID containing the milestones to delete
 * @param releasePhaseId - Optional release phase ID for phase-specific deletion
 * @returns Promise containing the deletion result
 */
export const deleteMilestones = async (
  issueId: string,
  releasePhaseId?: string
): Promise<{
  success: boolean;
  status?: number;
  error?: string;
  message?: string;
  alreadyNotified?: boolean;
  /** Raw API body — may include moment/notifications envelope for slideout refresh */
  data?: unknown;
}> => {
  try {
    const url = `${API_BASE_URL}/milestones/${encodeURIComponent(issueId)}`;

    // Prepare request options
    const requestOptions: RequestInit = {
      method: 'DELETE'
    };

    // If releasePhaseId is provided, include it in the request body
    if (releasePhaseId) {
      requestOptions.body = JSON.stringify({
        phaseId: releasePhaseId
      });
    }

    const responseData: any = await request(url, requestOptions, {
      showCustomError: true
    });

    // Check if data.deleted is false or data is null
    if (!responseData?.data || responseData.data.deleted === false) {
      throw new Error(responseData?.message || 'Failed to delete milestones');
    }

    return {
      success: true,
      status: 200,
      data: responseData
    };
  } catch (error) {
    if (error instanceof ResponseError) {
      if (error.response.status === 401) {
        handle401Error('Deleting milestones');
        return { success: false, status: 401, alreadyNotified: true };
      }

      const responseData = await error.response.json().catch(() => null);
      const message =
        typeof responseData?.message === 'string' && responseData.message.trim()
          ? responseData.message
          : 'Failed to delete milestones';
      return {
        success: false,
        status: error.response.status,
        error: message,
        message
      };
    }

    const errorMessage =
      error instanceof Error ? error.message : 'Failed to delete milestones';
    return {
      success: false,
      error: errorMessage,
      message: errorMessage
    };
  }
};

export interface MilestoneApprover {
  level: string; // "L1" or "L2"
  uid: string;
  name: string | null;
  imageUrl: string;
}

/** One resolved SLC-team Cc recipient (same backend shape as MilestoneApprover, but uid/imageUrl
 *  are genuinely absent for entries that don't resolve to a known Roadmap user — e.g. a
 *  distribution group like Grp-ATS-ROADMAP-ENGG@adobe.com — in which case name is the raw address. */
export interface SlcTeamContact {
  uid?: string | null;
  name: string;
  imageUrl?: string | null;
}

export interface DefaultApproversResult {
  milestoneName: string;
  teamKey: string | null;
  productGroups: string[];
  approvalRequired: boolean;
  documentLinkRequired: boolean;
  approvers: MilestoneApprover[];
  /** Fixed SLC-team Cc recipients every approval-request email Cc's, regardless of what the user
   *  picks in the Cc field — one entry per address configured in milestone-approval.slc-team-cc
   *  (which may be a CSV of multiple individuals/groups, not just one) — shown so the user can
   *  preview them before Send for Review. */
  slcTeamCc?: SlcTeamContact[];
}

/**
 * Fetches default approvers for a milestone from the template timeline config.
 * Call when the user selects an approval-requiring status so the approver field
 * can be pre-populated. The user can accept or edit before saving.
 */
export const fetchDefaultApprovers = async (
  milestoneName: string,
  productGroups: string[],
  targetStatus?: string,
  tier?: string
): Promise<DefaultApproversResult | null> => {
  try {
    const params = new URLSearchParams({ milestoneName });
    if (productGroups.length > 0) {
      params.set('productGroups', productGroups.join(','));
    }
    if (targetStatus) {
      params.set('targetStatus', targetStatus);
    }
    if (tier) {
      params.set('tier', tier);
    }
    const url = `${API_BASE_URL}/milestones/default-approvers?${params.toString()}`;
    const responseData: any = await request(url, { method: 'GET' });
    return (responseData?.data as DefaultApproversResult) ?? null;
  } catch (error) {
    console.error('[fetchDefaultApprovers] Failed:', error);
    return null;
  }
};

/** Real-time status of a previously sent Async milestone approval email. */
export interface MilestoneApprovalStatusResult {
  issueId: string;
  milestoneId: string;
  milestoneName: string;
  emailSentStatus:
    | 'PENDING'
    | 'APPROVED'
    | 'NOT_APPROVED'
    | 'RE_APPROVED'
    | 'NOT_SENT';
  canSendForReview: boolean;
}

/**
 * Resolves the real-time approval-email status for a milestone that was already
 * sent for approval. Call when the Edit Milestone modal opens on such a
 * milestone so it can react to approver action taken outside this session, or
 * to a send that never actually went out. Throws on failure — callers decide
 * how to surface the error (e.g. a toast).
 */
export const fetchMilestoneApprovalStatus = async (
  issueId: string,
  milestoneId: string,
  milestoneName: string
): Promise<MilestoneApprovalStatusResult> => {
  const params = new URLSearchParams({ milestoneName });
  const url = `${API_BASE_URL}/milestones/${encodeURIComponent(
    issueId
  )}/${encodeURIComponent(milestoneId)}/approval-status?${params.toString()}`;
  let responseData: any = await request(url, { method: 'GET' });
  //    responseData ={
  //     "status": 200,
  //     "message": "Approval status resolved",
  //     "data": {
  //         "issueId": "ARM-2019",
  //         "milestoneId": "MLS-2236",
  //         "milestoneName": "BRND",
  //         "emailSentStatus": "NOT_SENT",
  //         "canSendForReview": false
  //     },
  //     "errors": null
  // }
  return responseData?.data as MilestoneApprovalStatusResult;
};

/**
 * Cache for API responses to avoid unnecessary requests
 */
const momentCache = new Map<string, { data: MomentData; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Cached version of fetchMomentData
 * @param issueId - The unique identifier for the moment/issue
 * @param forceRefresh - Whether to bypass cache and fetch fresh data
 */
export const fetchMomentDataCached = async (
  issueId: string,
  forceRefresh = false
): Promise<MomentApiResponse> => {
  // Check cache first (unless force refresh is requested)
  if (!forceRefresh) {
    const cached = momentCache.get(issueId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return {
        success: true,
        data: cached.data
      };
    }
  }

  // Fetch fresh data
  const result = await fetchMomentData(issueId);

  // Cache successful responses
  if (result.success && result.data) {
    momentCache.set(issueId, {
      data: result.data,
      timestamp: Date.now()
    });
  }

  return result;
};

/**
 * Clear cached data for a specific moment or all moments
 * @param issueId - Specific moment to clear, or undefined to clear all
 */
export const clearMomentCache = (issueId?: string): void => {
  if (issueId) {
    momentCache.delete(issueId);
  } else {
    momentCache.clear();
  }
};

// ========================= EVENTS API =========================

/**
 * Interface for event data returned by the events API
 */
export interface EventData {
  id: string;
  name: string;
  status?: string;
  description?: string;
  isActive?: boolean;
}

/**
 * API response wrapper for events
 */
export interface EventsApiResponse {
  success: boolean;
  data?: EventData[];
  error?: string;
  message?: string;
}

/**
 * Fetches all active events from the API endpoint
 * @returns Promise containing the events data or error
 */
export const fetchEvents = async (): Promise<EventsApiResponse> => {
  try {
    // Construct the API URL
    const url = `${API_BASE_URL}/moments/type/event`;

    // Get auth headers and log if token is present
    const headers = getAuthHeaders();

    // Make the API call with authentication headers
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include' // Include cookies for authentication
    });

    // Check if the response is successful
    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `❌ Events API Error: ${response.status} ${response.statusText}`,
        errorText
      );

      // Handle specific error codes
      switch (response.status) {
        case 404:
          return {
            success: false,
            error: 'EVENTS_NOT_FOUND',
            message: 'Events endpoint not found'
          };
        case 403:
          handle403Error('Fetching events');
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'You do not have permission to access events'
          };
        case 401:
          handle401Error('Fetching events');
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required to access events'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: 'Internal server error. Please try again later.'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: `Events API request failed with status ${response.status}`
          };
      }
    }

    // Parse the JSON response
    const data = await response.json();

    // Handle different response formats
    let events: EventData[] = [];

    if (Array.isArray(data)) {
      events = data;
    } else if (data && typeof data === 'object') {
      // Try to find an array property in the response
      const arrayProps = Object.keys(data).filter(key =>
        Array.isArray(data[key])
      );
      if (arrayProps.length > 0) {
        // Use the first array property found (prefer 'events' if it exists)
        const arrayProp = arrayProps.includes('events')
          ? 'events'
          : arrayProps.includes('items')
          ? 'items'
          : arrayProps[0];
        events = data[arrayProp];
      } else {
        console.error('❌ Invalid events API response: no array found', data);
        return {
          success: false,
          error: 'INVALID_RESPONSE',
          message: 'Invalid response format from events API'
        };
      }
    }

    // Validate events data structure and normalize format
    const normalizedEvents: EventData[] = events
      .map((event: any) => ({
        id: event.id || event.value || '',
        name: event.name || event.label || event.title || '',
        status: event.status || 'active',
        description: event.description || '',
        isActive: event.isActive !== false // Default to true unless explicitly false
      }))
      .filter(event => event.id && event.name); // Filter out invalid entries

    return {
      success: true,
      data: normalizedEvents
    };
  } catch (error) {
    console.error('❌ Network error fetching events:', error);

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.'
      };
    }

    return {
      success: false,
      error: 'UNKNOWN_ERROR',
      message:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
};

/**
 * Cache for events API responses
 */
const eventsCache = new Map<string, { data: EventData[]; timestamp: number }>();
const EVENTS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (events change less frequently)

/**
 * In-flight request tracker to prevent duplicate API calls
 */
let eventsInflightRequest: Promise<EventsApiResponse> | null = null;

/**
 * Cached version of fetchEvents with request deduplication
 * @param forceRefresh - Whether to bypass cache and fetch fresh data
 */
export const fetchEventsCached = async (
  forceRefresh = false
): Promise<EventsApiResponse> => {
  const cacheKey = 'all_events';

  // Check cache first (unless force refresh is requested)
  if (!forceRefresh) {
    const cached = eventsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < EVENTS_CACHE_DURATION) {
      return {
        success: true,
        data: cached.data
      };
    }
  }

  // If a request is already in progress, return that promise instead of making a new request
  if (eventsInflightRequest) {
    return eventsInflightRequest;
  }

  // Start a new fetch and store the promise
  eventsInflightRequest = fetchEvents()
    .then(result => {
      // Cache successful responses
      if (result.success && result.data) {
        eventsCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
      }
      return result;
    })
    .finally(() => {
      // Clear the in-flight tracker when done
      eventsInflightRequest = null;
    });

  return eventsInflightRequest;
};

/**
 * Clear events cache
 */
export const clearEventsCache = (): void => {
  eventsCache.clear();
};

// ========================= FEATURES API (per Moment) =========================

// Features API moved to featuresApi.ts; re-export for backward compatibility
export { fetchFeaturesByIssue } from './featuresApi';

/**
 * Interface for Parent Moment data structure
 */
export interface ParentMomentData {
  id: string;
  name: string;
  status?: string;
  description?: string;
  isActive?: boolean;
}

/**
 * Interface for Parent Moments API response
 */
export interface ParentMomentsApiResponse {
  success: boolean;
  data?: ParentMomentData[];
  error?: string;
  message?: string;
}

/**
 * Fetches all available parent moments from the API endpoint
 * @returns Promise containing the parent moments data or error
 */
export const fetchParentMoments =
  async (): Promise<ParentMomentsApiResponse> => {
    try {
      // Construct the API URL - using marketing-moment endpoint for parent moments
      const url = `${API_BASE_URL}/moments/type/marketing-moment`;

      // Get auth headers and log if token is present
      const headers = getAuthHeaders();
      // Make the API call with authentication headers
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include' // Include cookies for authentication
      });

      // Check if the response is successful
      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ Parent Moments API Error: ${response.status} ${response.statusText}`,
          errorText
        );

        // Handle specific error codes
        switch (response.status) {
          case 404:
            return {
              success: false,
              error: 'PARENT_MOMENTS_NOT_FOUND',
              message: 'Parent moments endpoint not found'
            };
          case 403:
            return {
              success: false,
              error: 'ACCESS_DENIED',
              message: 'You do not have permission to access parent moments'
            };
          case 401:
            return {
              success: false,
              error: 'UNAUTHORIZED',
              message: 'Authentication required to access parent moments'
            };
          case 500:
            return {
              success: false,
              error: 'SERVER_ERROR',
              message: 'Internal server error. Please try again later.'
            };
          default:
            return {
              success: false,
              error: 'API_ERROR',
              message: `Parent moments API request failed with status ${response.status}`
            };
        }
      }

      // Parse the JSON response
      const data = await response.json();

      // Handle different response formats
      let parentMoments: ParentMomentData[] = [];

      if (Array.isArray(data)) {
        parentMoments = data;
      } else if (data && typeof data === 'object') {
        // Try to find an array property in the response
        const arrayProps = Object.keys(data).filter(key =>
          Array.isArray(data[key])
        );
        if (arrayProps.length > 0) {
          // Use the first array property found (prefer 'parentMoments' if it exists)
          const arrayProp = arrayProps.includes('parentMoments')
            ? 'parentMoments'
            : arrayProps.includes('moments')
            ? 'moments'
            : arrayProps.includes('items')
            ? 'items'
            : arrayProps[0];
          parentMoments = data[arrayProp];
        } else {
          console.error(
            '❌ Invalid parent moments API response: no array found',
            data
          );
          return {
            success: false,
            error: 'INVALID_RESPONSE',
            message: 'Invalid response format from parent moments API'
          };
        }
      }

      // Validate parent moments data structure and normalize format
      const normalizedParentMoments: ParentMomentData[] = parentMoments
        .map((moment: any) => ({
          id: moment.id || moment.value || '',
          name:
            moment.name || moment.label || moment.title || moment.summary || '',
          status: moment.status || 'active',
          description: moment.description || '',
          isActive: moment.isActive !== false // Default to true unless explicitly false
        }))
        .filter(moment => moment.id && moment.name); // Filter out invalid entries

      return {
        success: true,
        data: normalizedParentMoments
      };
    } catch (error) {
      console.error('❌ Network error fetching parent moments:', error);
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
      };
    }
  };

/**
 * Cache for parent moments API responses
 */
const parentMomentsCache = new Map<
  string,
  { data: ParentMomentData[]; timestamp: number }
>();
const PARENT_MOMENTS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (parent moments change less frequently)

/**
 * In-flight request tracker to prevent duplicate API calls
 */
let parentMomentsInflightRequest: Promise<ParentMomentsApiResponse> | null =
  null;

/**
 * Cached version of fetchParentMoments with request deduplication
 * @param forceRefresh - Whether to bypass cache and fetch fresh data
 */
export const fetchParentMomentsCached = async (
  forceRefresh = false
): Promise<ParentMomentsApiResponse> => {
  const cacheKey = 'all_parent_moments';

  // Check cache first (unless force refresh is requested)
  if (!forceRefresh) {
    const cached = parentMomentsCache.get(cacheKey);
    if (
      cached &&
      Date.now() - cached.timestamp < PARENT_MOMENTS_CACHE_DURATION
    ) {
      return {
        success: true,
        data: cached.data
      };
    }
  }

  // If a request is already in progress, return that promise instead of making a new request
  if (parentMomentsInflightRequest) {
    return parentMomentsInflightRequest;
  }

  // Start a new fetch and store the promise
  parentMomentsInflightRequest = fetchParentMoments()
    .then(result => {
      // Cache successful responses
      if (result.success && result.data) {
        parentMomentsCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
      }
      return result;
    })
    .finally(() => {
      // Clear the in-flight tracker when done
      parentMomentsInflightRequest = null;
    });

  return parentMomentsInflightRequest;
};

/**
 * Clear parent moments cache
 */
export const clearParentMomentsCache = (): void => {
  parentMomentsCache.clear();
};

/** RSD change log item */
export interface ChangeLogFeatureRef {
  id: string;
  name: string;
  /** Owning product (e.g. "Illustrator Desktop (Ai)"). Optional — older rows may omit it. */
  product?: string;
}

export interface ChangeLogItem {
  changeType: string;
  changeTypeCode?: string;
  changeDate: string;
  changedBy: string;
  /**
   * How the change was made: "pat" (Personal Access Token) or "mcp" (MCP write tool / AI
   * assistant). Undefined for a direct browser session — the common case, not distinctly labeled.
   */
  authVia?: string;
  phase: string;
  from: string;
  to: string;
  feature?: ChangeLogFeatureRef | null;
  link?: string;
  /**
   * Broad section ("General Info", "Overview", "Milestones", "Primary Contacts",
   * "DACI Contacts", "Decisions", "Additional Info", "Features", "Moment"). Optional.
   */
  section?: string;
  /**
   * The specific subject of the row — milestone name, contact name (+ role), decision
   * title, link title, phase name, or feature name. Null for section-level edits
   * (title / headline / tier / dates).
   */
  item?: string;
}

export interface ChangeLogResponse {
  issueId: string;
  items: ChangeLogItem[];
}

function parseChangeLogItem(raw: unknown): ChangeLogItem | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const changeType = o.changeType;
  const changeDate = o.changeDate;
  if (typeof changeType !== 'string' || typeof changeDate !== 'string') {
    return null;
  }
  let feature: ChangeLogFeatureRef | null | undefined;
  if (o.feature != null && typeof o.feature === 'object') {
    const f = o.feature as Record<string, unknown>;
    if (typeof f.id === 'string' && typeof f.name === 'string') {
      feature = {
        id: f.id,
        name: f.name,
        ...(typeof f.product === 'string' && f.product
          ? { product: f.product }
          : {})
      };
    }
  }
  return {
    changeType,
    changeTypeCode:
      typeof o.changeTypeCode === 'string' ? o.changeTypeCode : undefined,
    changeDate,
    changedBy: typeof o.changedBy === 'string' ? o.changedBy : '',
    authVia: typeof o.authVia === 'string' ? o.authVia : undefined,
    phase: typeof o.phase === 'string' ? o.phase : '',
    from: typeof o.from === 'string' ? o.from : '',
    to: typeof o.to === 'string' ? o.to : '',
    feature: feature ?? null,
    link: typeof o.link === 'string' ? o.link : undefined,
    section: typeof o.section === 'string' ? o.section : undefined,
    item: typeof o.item === 'string' ? o.item : undefined
  };
}

/** Runtime validation for change-log JSON (response.json() is untyped). */
function parseChangeLogResponseJson(raw: unknown): ChangeLogResponse | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.issueId !== 'string') {
    return null;
  }
  const items: ChangeLogItem[] = [];
  if (Array.isArray(o.items)) {
    for (const el of o.items) {
      const row = parseChangeLogItem(el);
      if (row) {
        items.push(row);
      }
    }
  }
  return { issueId: o.issueId, items };
}

/** Query preset for GET .../change-log (from /change-log-presets). */
export type ChangeLogPresetId = 'LAST_7_DAYS' | 'LAST_MONTH' | 'ALL_TIME';

/**
 * GET /moments/{issueId}/change-log — RSD change history (window applied on the server).
 * Optional `preset` (e.g. LAST_MONTH, ALL_TIME). If omitted, the server defaults (typically last 7 days).
 */
export const fetchChangeLog = async (
  issueId: string,
  preset?: ChangeLogPresetId
): Promise<{
  success: boolean;
  data?: ChangeLogResponse;
  error?: string;
  message?: string;
}> => {
  try {
    const path = `${API_BASE_URL}/moments/${encodeURIComponent(
      issueId
    )}/change-log`;
    const url =
      preset !== undefined
        ? `${path}?${new URLSearchParams({ preset }).toString()}`
        : path;
    const headers = getAuthHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error(
        `❌ Change log API error: ${response.status} ${response.statusText}`,
        errorText
      );

      switch (response.status) {
        case 404:
          return {
            success: false,
            error: 'NOT_FOUND',
            message: 'Change log not found for this item'
          };
        case 403:
          handle403Error(`Fetching change log for ${issueId}`);
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'You do not have permission to view the change log'
          };
        case 401:
          handle401Error(`Fetching change log for ${issueId}`);
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: 'Could not load change log. Please try again.'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: `Request failed with status ${response.status}`
          };
      }
    }

    const json: unknown = await response.json();
    const parsed = parseChangeLogResponseJson(json);
    if (!parsed) {
      return {
        success: false,
        error: 'INVALID_RESPONSE',
        message: 'Invalid response format from server'
      };
    }

    return {
      success: true,
      data: parsed
    };
  } catch (error) {
    console.error('❌ Network error fetching change log:', error);
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Network error. Check your connection and try again.'
      };
    }
    return {
      success: false,
      error: 'UNKNOWN_ERROR',
      message:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
};
